"use client";

import { useState, useEffect } from "react";
import { CheckCircle2, Clock, AlertCircle, ArrowRight, Check, X, MessageSquare, Send, Edit } from "lucide-react";
import apiClient from "@/services/api";

interface RecentTransaction {
  id: string;
  customer: string;
  email: string;
  amount: string;
  status: "sent" | "draft" | "pending";
  date: string;
  platform: string;
  description: string;
}

export function RecentSales() {
  const [transactions, setTransactions] = useState<RecentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch real transactions from backend
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📊 Fetching recent transactions...');
      const response = await apiClient.get('/backend/api/credits/transactions?page=1&limit=8');

      console.log('📊 Raw response:', response.data);

      // Handle the response structure from transactions API
      let transactions = [];

      if (response.data.success && response.data.data) {
        // The API returns: { success: true, data: { transactions: [...], pagination: {...}, summary: {...} } }
        transactions = response.data.data.transactions || [];
      } else {
        throw new Error('Invalid response format');
      }

      // Ensure transactions is an array
      if (!Array.isArray(transactions)) {
        console.warn('Transactions data is not an array:', transactions);
        transactions = [];
      }

      // Transform credit transactions into display format
      const recentTransactions: RecentTransaction[] = transactions
        .slice(0, 8) // Get latest 8
        .map((transaction: any, index: number) => {
          // Use the actual credit transaction data structure
          let amount = '';
          let status: 'sent' | 'draft' | 'pending' = 'pending';
          let customerMessage = '';
          let responseMessage = '';

          // Determine credit amount based on transaction type and actual data
          if (transaction.type === 'usage' && transaction.creditsUsed > 0) {
            // Usage transaction - credits were deducted
            amount = `-${transaction.creditsUsed} Credit${transaction.creditsUsed > 1 ? 's' : ''}`;
            status = 'sent'; // Usage means response was sent
          } else if (transaction.type === 'refill' || transaction.type === 'bonus') {
            // Credit addition transaction
            amount = `+${transaction.creditsAdded || 0} Credit${(transaction.creditsAdded || 0) > 1 ? 's' : ''}`;
            status = 'sent'; // Credit addition is completed
          } else if (transaction.type === 'adjustment') {
            // Credit adjustment (could be positive or negative)
            if (transaction.creditsAdded > 0) {
              amount = `+${transaction.creditsAdded} Credit${transaction.creditsAdded > 1 ? 's' : ''}`;
            } else if (transaction.creditsUsed > 0) {
              amount = `-${transaction.creditsUsed} Credit${transaction.creditsUsed > 1 ? 's' : ''}`;
            } else {
              amount = '0 Credits';
            }
            status = 'sent'; // Adjustment is completed
          } else {
            // Fallback for unknown types or zero amounts
            amount = '0 Credits';
            status = 'pending';
          }

          // Extract actual message content
          if (transaction.messageContent) {
            // Try to parse if it's JSON
            try {
              const parsed = JSON.parse(transaction.messageContent);
              customerMessage = parsed.customerMessage || parsed.message || '';
              responseMessage = parsed.response || parsed.generatedResponse || '';
            } catch {
              // If not JSON, use as is
              customerMessage = transaction.messageContent;
              responseMessage = transaction.response || '';
            }
          }

          // Fallback to description fields
          if (!customerMessage) {
            customerMessage = transaction.description || transaction.customerMessage || 'Customer inquiry';
          }
          if (!responseMessage) {
            responseMessage = transaction.response || transaction.generatedResponse || 'Response generated';
          }

          // Truncate messages for display
          const truncateText = (text: string, maxLength: number = 50) => {
            return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
          };

          return {
            id: transaction._id || `trans-${index}`,
            customer: truncateText(customerMessage),
            email: truncateText(responseMessage),
            amount: amount,
            status: status,
            date: transaction.createdAt ? new Date(transaction.createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            }) : 'Recent',
            platform: transaction.platform || 'System',
            description: transaction.type === 'usage' ? 'Response sent' :
                        transaction.type === 'refill' ? 'Credits added' :
                        transaction.type === 'bonus' ? 'Bonus credits' :
                        transaction.type === 'adjustment' ? 'Credit adjustment' : 'Transaction'
          };
        });

      setTransactions(recentTransactions);
      console.log('✅ Recent transactions loaded:', recentTransactions.length);
    } catch (error: any) {
      console.error('❌ Error fetching transactions:', error);

      // Try fallback to comments API (for demo purposes)
      try {
        console.log('🔄 Trying fallback to comments API...');
        const fallbackResponse = await apiClient.get('/backend/api/comments');

        let comments = [];
        if (Array.isArray(fallbackResponse.data)) {
          comments = fallbackResponse.data;
        } else if (fallbackResponse.data.success) {
          comments = fallbackResponse.data.data || [];
        }

        // Ensure comments is an array
        if (!Array.isArray(comments)) {
          comments = [];
        }

        // Transform comments into transaction format (same logic as response page)
        const fallbackTransactions: RecentTransaction[] = comments
          .slice(0, 8)
          .map((comment: any, index: number) => {
            // Use the same status logic as response page
            const status = comment.status || 'pending';
            let amount = '';

            if (status === 'sent') {
              amount = '-1 Credit'; // Sent responses cost credits
            } else if (status === 'draft') {
              amount = '0 Credits'; // Drafts don't cost credits yet
            } else if (status === 'pending') {
              amount = '0 Credits'; // Pending responses don't cost credits yet
            } else {
              amount = '0 Credits'; // Default fallback
            }

            // Extract message content
            const customerMessage = comment.content || comment.reviewer_display_name || 'Customer inquiry';
            const responseMessage = comment.updatedContent || 'Response generated';

            // Truncate messages
            const truncateText = (text: string, maxLength: number = 50) => {
              return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
            };

            return {
              id: comment._id || `trans-${index}`,
              customer: truncateText(customerMessage),
              email: truncateText(responseMessage),
              amount: amount,
              status: status,
              date: comment.createdAt ? new Date(comment.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              }) : comment.time ? new Date(comment.time).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              }) : 'Recent',
              platform: comment.platform || 'Platform',
              description: `${status === 'sent' ? 'Sent' : status === 'draft' ? 'Draft' : 'Pending'} response`
            };
          });

        setTransactions(fallbackTransactions);
        console.log('✅ Fallback transactions loaded:', fallbackTransactions.length);
      } catch (fallbackError: any) {
        console.error('❌ Fallback also failed:', fallbackError);
        setError(error.message || 'Failed to load transactions');
        setTransactions([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "sent":
        return (
          <div className="h-9 w-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <Send className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </div>
        );
      case "draft":
        return (
          <div className="h-9 w-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <Edit className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </div>
        );
      case "pending":
        return (
          <div className="h-9 w-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </div>
        );
      default:
        return (
          <div className="h-9 w-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <MessageSquare className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </div>
        );
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "sent":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            <Send className="h-3 w-3" />
            Sent
          </span>
        );
      case "draft":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
            <Edit className="h-3 w-3" />
            Draft
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
            <Clock className="h-3 w-3" />
            Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
            <MessageSquare className="h-3 w-3" />
            Review
          </span>
        );
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200 dark:border-gray-800 pb-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs font-medium text-gray-500 dark:text-gray-400">
            <div>Customer Message</div>
            <div>Status</div>
            <div>Date</div>
            <div className="text-right">Credits</div>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200 dark:border-gray-800 pb-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs font-medium text-gray-500 dark:text-gray-400">
            <div>Customer Message</div>
            <div>Status</div>
            <div>Date</div>
            <div className="text-right">Credits</div>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <p className="text-gray-500 dark:text-gray-400 mb-2">{error}</p>
            <button
              onClick={fetchTransactions}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border-b border-gray-200 dark:border-gray-800 pb-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs font-medium text-gray-500 dark:text-gray-400">
          <div>Customer Message</div>
          <div>Status</div>
          <div>Date</div>
          <div className="text-right">Credits</div>
        </div>
      </div>

      {transactions.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No recent transactions</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Transactions will appear here when you start responding to reviews</p>
          </div>
        </div>
      ) : (
        transactions.map((transaction) => (
          <div
            key={transaction.id}
            className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-center py-3 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              {getStatusIcon(transaction.status)}
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white" title={transaction.customer}>
                  {transaction.customer}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400" title={transaction.email}>
                  Response: {transaction.email}
                </p>
              </div>
            </div>
            <div>
              {getStatusBadge(transaction.status)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {transaction.date}
            </div>
            <div className="text-sm font-medium text-right text-gray-900 dark:text-white">
              {transaction.amount}
            </div>
          </div>
        ))
      )}

      {transactions.length > 0 && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={fetchTransactions}
            className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 transform active:scale-95 text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-800"
          >
            <span>Refresh</span>
          </button>
        </div>
      )}
    </div>
  );
}
